import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, Settings } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NAV_ITEMS } from "./navItems";
import LogoImage from "@/components/LogoImage";
import { AvatarView } from "@/pages/Profile";

export default function Header({ branding, user, onLogout }) {
  const navigate = useNavigate();
  return (
    <header
      className={`${branding.header_sticky === false ? "relative" : "sticky top-0"} z-50 backdrop-blur-xl border-b border-[#3F3F4C]`}
      style={{ backgroundColor: `rgba(18, 18, 20, ${1 - (branding.nav_transparency ?? 10) / 100})` }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <NavLink to="/" className="flex items-center gap-2 leading-none min-w-0 shrink" data-testid="nav-logo">
          {branding.mobile_logo_url && (
            <LogoImage
              src={branding.mobile_logo_url}
              alt="Logo mobile"
              className="object-contain rounded sm:hidden max-h-12"
              style={{ height: branding.logo_height || 32, width: branding.logo_width ? branding.logo_width : "auto" }}
            />
          )}
          {branding.logo_url && (
            <LogoImage
              src={branding.logo_url}
              alt="Logo"
              className={`object-contain rounded ${branding.mobile_logo_url ? "hidden sm:block" : ""}`}
              style={{
                height: branding.logo_height || 32,
                width: branding.logo_width ? branding.logo_width : "auto",
                marginRight: branding.logo_margin ?? 8,
              }}
            />
          )}
          {!(branding.logo_url || branding.mobile_logo_url) && (
            <span className="flex flex-col">
              <span className="font-racing font-black text-lg sm:text-xl text-[var(--brand-primary)] tracking-wider">
                FANTASY<span className="text-white">RACE</span>
              </span>
              <span className="font-body-sport text-[9px] sm:text-[10px] tracking-[0.25em] text-gray-400 uppercase">
                69° Salita dei Monti Iblei
              </span>
            </span>
          )}
        </NavLink>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-body-sport text-xs font-bold tracking-widest uppercase transition-colors ${
                  isActive ? "bg-[#FF1E27]/15 text-[#FF1E27]" : "text-gray-400 hover:text-white"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-body-sport text-xs font-bold tracking-widest uppercase transition-colors ${
                  isActive ? "bg-[#FFB703]/15 text-[#FFB703]" : "text-[#FFB703]/70 hover:text-[#FFB703]"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4" />
              Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-button"
                title="Account"
                className="rounded-full border-2 border-[#3F3F4C] hover:border-[#FF1E27] transition-colors p-0.5 cursor-pointer"
              >
                <AvatarView user={user} size={32} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 bg-[#1A1A1E] border-[#3F3F4C] text-gray-200" data-testid="user-menu">
              <DropdownMenuLabel className="flex items-center gap-3 py-3">
                <AvatarView user={user} size={40} />
                <span className="min-w-0">
                  <span data-testid="nav-username" className="block font-body-sport font-bold text-white text-sm truncate">
                    {user?.username}
                  </span>
                  <span className="block text-[11px] text-gray-500 truncate">{user?.email}</span>
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#3F3F4C]" />
              <DropdownMenuItem
                data-testid="profile-settings-link"
                onClick={() => navigate("/profile")}
                className="gap-2 cursor-pointer font-body-sport text-xs font-bold tracking-widest uppercase focus:bg-[#242429] focus:text-white"
              >
                <Settings className="w-4 h-4 text-[#FFB703]" /> Impostazioni Profilo
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="logout-button"
                onClick={onLogout}
                className="gap-2 cursor-pointer font-body-sport text-xs font-bold tracking-widest uppercase text-[#FF1E27] focus:bg-[#FF1E27]/10 focus:text-[#FF1E27]"
              >
                <LogOut className="w-4 h-4" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
